import type { Tone } from './state.ts';
import styles from './StatusDot.module.css';

/* ==========================================================================
   Status encoding, cheapest form first.

   The console's rule (see THE BAR §5): reach for the highest-density encoding
   that fits — a matrix cell, then a 2px row edge bar, then a 6px dot. A filled
   badge is the last resort and is reserved for singular, page-level facts.
   ========================================================================== */

interface DotProps {
  tone: Tone;
  hollow?: boolean | undefined;
  /** Only ever true for a stream that is actually live right now. */
  pulse?: boolean | undefined;
  title?: string | undefined;
}

/** A 6px dot. Pairs with a label; never carries meaning on its own. */
export function StatusDot({ tone, hollow = false, pulse = false, title }: DotProps) {
  return (
    <span
      className={styles.dot}
      data-tone={tone}
      data-hollow={hollow}
      data-pulse={pulse}
      title={title}
      aria-hidden="true"
    />
  );
}

/** A 2px bar at a row's leading edge, full row height. */
export function StatusEdge({ tone, hollow = false }: { tone: Tone; hollow?: boolean | undefined }) {
  return (
    <span className={styles.edge} data-tone={tone} data-hollow={hollow} aria-hidden="true" />
  );
}

/** One reading in a metric × robot matrix. 13px square on a 19px pitch. */
export function StatusCell({
  tone,
  hollow = false,
  title,
}: {
  tone: Tone;
  hollow?: boolean | undefined;
  title?: string | undefined;
}) {
  return (
    <span
      className={styles.cell}
      data-tone={tone}
      data-hollow={hollow}
      title={title}
      role="img"
      aria-label={title}
    />
  );
}
